// src/app/components/admin/smoke-calibration-setting.tsx
//
// Platform section of Global Settings: the two automatic steps of a Smoke
// calibration (#138), the diagnosis and the re-run, and the spend cap over one
// calibration's rounds. Backend: app/services/smoke_calibration/config.py.

import { useEffect, useState } from "react";
import { Check, Loader2, RotateCcw } from "lucide-react";

import {
  fetchSmokeCalibrationSettings, saveSmokeCalibrationSettings, type SmokeCalibrationSettings,
} from "@/api/onboarding-measurement-api";

const muted = { fontSize: "var(--text-xs)", color: "var(--muted-foreground)", lineHeight: 1.5 } as const;
const field = {
  fontSize: "var(--text-sm)", height: 32, padding: "0 10px", borderRadius: 8, width: 120,
  border: "1px solid var(--border)", backgroundColor: "var(--background)", color: "var(--foreground)",
} as const;

export function SmokeCalibrationSetting() {
  const [settings, setSettings] = useState<SmokeCalibrationSettings | null>(null);
  const [draft, setDraft] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const adopt = (s: SmokeCalibrationSettings) => {
    setSettings(s);
    setDraft(String(s.spend_cap_usd));
  };

  useEffect(() => {
    fetchSmokeCalibrationSettings().then(adopt).catch((e) => setErr(String(e?.message || e)));
  }, []);

  async function save(values: Parameters<typeof saveSmokeCalibrationSettings>[0]) {
    setSaving(true);
    setErr("");
    setSaved(false);
    try {
      adopt(await saveSmokeCalibrationSettings(values));
      setSaved(true);
    } catch (e: any) {
      setErr(String(e?.message || e));
    } finally {
      setSaving(false);
    }
  }

  const parsed = Number(draft);
  const valid = draft.trim() !== "" && Number.isFinite(parsed) && parsed > 0;
  const changed = settings != null && draft !== String(settings.spend_cap_usd);

  return (
    <div className="py-6">
      <div className="max-w-2xl">
        <h2 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)", color: "var(--foreground)" }}>
          Smoke calibration
        </h2>
        <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", marginTop: 4 }}>
          Two calibration steps run without anyone pressing a button: a diagnosis of each flagged belief, which makes
          one model call per belief, and the re-run after accepted fixes are applied. A change applies to the next
          round, with no restart. What an admin starts from the calibration panel still runs.
        </p>

        {err && (
          <div className="mt-4 rounded-lg px-3 py-2"
            style={{ fontSize: "var(--text-sm)", color: "var(--error-text)", backgroundColor: "var(--error-bg)" }}>
            {err}
          </div>
        )}
        {!settings && !err && (
          <p className="mt-5 inline-flex items-center gap-2" style={muted}>
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </p>
        )}

        {settings && (
          <div className="mt-5 flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <label className="flex items-center gap-2" style={{ fontSize: "var(--text-sm)", color: "var(--foreground)" }}>
                <input type="checkbox" checked={settings.diagnosis_enabled} disabled={saving}
                  onChange={(e) => save({ diagnosis_enabled: e.target.checked })} />
                Automatic diagnosis is {settings.diagnosis_enabled ? "on" : "off"}
              </label>
              <span style={muted}>
                Off: a flagged belief shows its flag with no diagnosis, and no model call is made until an admin
                presses Diagnose.
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <label className="flex items-center gap-2" style={{ fontSize: "var(--text-sm)", color: "var(--foreground)" }}>
                <input type="checkbox" checked={settings.auto_rerun_enabled} disabled={saving}
                  onChange={(e) => save({ auto_rerun_enabled: e.target.checked })} />
                Automatic re-run is {settings.auto_rerun_enabled ? "on" : "off"}
              </label>
              <span style={muted}>
                Off: accepted fixes are still applied to the framework once every proposal is decided, but the next
                round waits until an admin presses Start the re-run.
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <span style={{ fontSize: "var(--text-sm)", color: "var(--foreground)", minWidth: 220 }}>
                Spend cap per calibration
              </span>
              <input inputMode="decimal" style={field} value={draft} disabled={saving}
                onChange={(e) => { setDraft(e.target.value); setSaved(false); }} />
              <span style={muted}>USD</span>
              <button type="button" disabled={!changed || !valid || saving} onClick={() => save({ spend_cap_usd: parsed })}
                className="rounded-lg border px-3 py-2"
                style={{ fontSize: "var(--text-sm)", cursor: changed && valid && !saving ? "pointer" : "default",
                         borderColor: "var(--border)", opacity: changed && valid ? 1 : 0.6 }}>
                {saving ? "Saving…" : "Save"}
              </button>
              <span style={{ ...muted, flexBasis: "100%" }}>
                Covers probing, judging and diagnosis across one calibration's rounds. A round whose estimate would
                cross it is refused.
                {!valid && draft.trim() !== "" ? " Enter a number greater than 0." : ""}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <span style={muted}>{settings.source === "admin" ? "Set here" : "Built-in defaults"}</span>
              {settings.source === "admin" && (
                <button type="button" disabled={saving}
                  onClick={() => save({ diagnosis_enabled: null, auto_rerun_enabled: null, spend_cap_usd: null })}
                  className="inline-flex items-center gap-1.5 rounded-lg px-2 py-2"
                  style={{ ...muted, cursor: saving ? "default" : "pointer", background: "transparent" }}>
                  <RotateCcw className="h-3.5 w-3.5" /> Reset to built-in defaults
                </button>
              )}
              {saved && (
                <span className="inline-flex items-center gap-1" style={{ ...muted, color: "var(--alpha-teal)" }}>
                  <Check className="h-3.5 w-3.5" /> Saved
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SmokeCalibrationSetting;
